import * as React from 'react';
import { createContext, useCallback, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useTracker } from '@coursera/event-pulse/react';

import type {
  CourseToolBarPluginContextType,
  Plugin,
} from 'bundles/course-v2/components/navigation/CourseToolBar/CourseToolBarTypes';
import { courseToolBarLocalStorageHandler } from 'bundles/course-v2/components/navigation/CourseToolBar/courseToolBarLocalStorageHandler';

export const CourseToolBarPluginContext = createContext<CourseToolBarPluginContextType | undefined>(undefined);

export const CourseToolBarPluginProvider = ({ children }: { children: ReactNode }) => {
  const toolBarStateProvider = courseToolBarLocalStorageHandler();
  const toolBarState = toolBarStateProvider.getState();
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  const [expanded, setExpanded] = useState((toolBarState?.expanded && toolBarState?.toolSelected === 'coach') ?? false);
  const [toolSelected, setToolSelected] = useState<string | null>('coach');
  const track = useTracker();
  const expandedRef = useRef(expanded);

  const closeTools = useCallback<() => void>(() => {
    setExpanded(false);
  }, []);

  const _setPlugins = useCallback<(newPlugins: Plugin[]) => void>(
    (newPlugins: Plugin[]) => {
      setPlugins(newPlugins);

      // track Coach open/close state when the plugin is loaded
      if (newPlugins.length > 0 && newPlugins.some((plugin) => plugin.name === 'coach')) {
        track('view_coach', {
          coachViewType: 'view_coach_chat',
          coachWidgetState: expandedRef.current ? 'open' : 'closed',
          coach: {
            location: 'toolbar',
          },
        });
      }
    },
    [setPlugins, track]
  );

  const _setExpanded = useCallback<(newExpanded: boolean) => void>(
    (newExpanded: boolean) => {
      setExpanded(newExpanded);
      expandedRef.current = newExpanded;
    },
    [setExpanded]
  );

  const _setToolSelected = useCallback<(tool: string | null) => void>(
    (tool: string | null) => {
      setToolSelected(tool);
    },
    [setToolSelected]
  );

  React.useEffect(() => {
    toolBarStateProvider.setState({ expanded, toolSelected });
  }, [toolBarStateProvider, expanded, toolSelected]);

  const contextValue = useMemo(
    () => ({
      plugins,
      setPlugins: _setPlugins,
      expanded,
      setExpanded: _setExpanded,
      toolSelected,
      setToolSelected: _setToolSelected,
      closeTools,
    }),
    [plugins, _setPlugins, expanded, _setExpanded, toolSelected, _setToolSelected, closeTools]
  );

  return <CourseToolBarPluginContext.Provider value={contextValue}>{children}</CourseToolBarPluginContext.Provider>;
};
